import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Filter, Grid, List } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import RecipeCard from '../components/recipe/RecipeCard'
import useRecipeStore from '../store/recipes'
import { cn } from '../lib/utils'

const RecipesPage = () => {
  const {
    filters,
    setFilters,
    getFilteredRecipes,
    fetchRecipes,
    isLoading
  } = useRecipeStore()

  const [viewMode, setViewMode] = useState('grid')
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  const filteredRecipes = getFilteredRecipes()

  // Mock categories for filtering
  const categories = [
    'all', 'breakfast', 'lunch', 'dinner', 'dessert', 'snacks', 'healthy', 'comfort'
  ]

  const difficulties = ['all', 'easy', 'medium', 'hard']
  const timeRanges = [
    { value: 'all', label: 'Any time' },
    { value: 'quick', label: 'Under 30min' },
    { value: 'medium', label: '30-60min' },
    { value: 'long', label: 'Over 1hr' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-dreamy">
            Recipe Collection
          </h1>
          <p className="text-gray-600 mt-1">
            Discover {filteredRecipes.length} magical recipes
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>

          <div className="flex bg-white/60 backdrop-blur-sm rounded-xl p-1">
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="px-3 py-1.5"
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="px-3 py-1.5"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <Input
          placeholder="Search recipes..."
          value={filters.searchQuery}
          onChange={(e) => setFilters({ searchQuery: e.target.value })}
          className="bg-white/90"
        />
      </div>

      {/* Filters */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
          className="card-dreamy"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            {/* Categories */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Category</h3>
              <div className="grid grid-cols-2 gap-2">
                {categories.map(category => (
                  <Button
                    key={category}
                    variant={filters.category === category ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setFilters({ category })}
                    className="justify-start capitalize"
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Difficulty */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Difficulty</h3>
              <div className="space-y-2">
                {difficulties.map(difficulty => (
                  <Button
                    key={difficulty}
                    variant={filters.difficulty === difficulty ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setFilters({ difficulty })}
                    className="w-full justify-start capitalize"
                  >
                    {difficulty}
                  </Button>
                ))}
              </div>
            </div>

            {/* Time */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Cooking Time</h3>
              <div className="space-y-2">
                {timeRanges.map(timeRange => (
                  <Button
                    key={timeRange.value}
                    variant={filters.time === timeRange.value ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setFilters({ time: timeRange.value })}
                    className="w-full justify-start"
                  >
                    {timeRange.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recipes Grid/List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-dreamy animate-pulse">
              <div className="aspect-[4/3] bg-gray-200 rounded-t-3xl"></div>
              <div className="p-6 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-display font-semibold text-gray-900 mb-2">
            No recipes found
          </h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search or filter criteria
          </p>
          <Button onClick={() => setFilters({ searchQuery: '', category: 'all', difficulty: 'all', time: 'all' })}>
            Clear Filters
          </Button>
        </div>
      ) : (
        <div className={cn(
          "grid gap-6",
          viewMode === 'grid'
            ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            : "grid-cols-1"
        )}>
          {filteredRecipes.map((recipe, index) => (
            <motion.div
              key={recipe.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <RecipeCard
                recipe={recipe}
                className={viewMode === 'list' ? 'flex-row' : ''}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default RecipesPage